/**
 * PublicProceduresService
 * Anonymous surface of Procedures Management: lookups, ministries and the public portal
 * (settings, categories, directory, published procedure, anonymous runs, AI chat).
 * Route: procedures/api/procedures (lookups) and procedures/api/procedures/public/*
 *
 * Works with no auth token (the HttpClient only adds Authorization when it has one).
 * Run actions need the plain token returned ONCE by `startPublicRun` (`ProcedureRunStartView.runToken`);
 * pass it as the `runToken` argument and it is sent as the X-Run-Token header.
 * Error codes to branch on arrive in CCMSError / errors[0] (PROCEDURES_ERROR_CODES), e.g. a private
 * procedure answers 403 `sign_in_required`.
 */
import { BaseService } from '../base/BaseService';
import { unwrapProceduresResponse as unwrap, proceduresRunTokenOptions as runTokenOptions } from './unwrapProceduresResponse';
/** Anonymous Procedures portal service (no JWT needed). */
export class PublicProceduresService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? PublicProceduresService.DEFAULT_SERVICE_PATH,
            basePath: PublicProceduresService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    enc(value) {
        return encodeURIComponent(value);
    }
    // ---- lookups ----
    /** GET api/procedures/lookups: every Procedures lookup table. */
    async getLookups() {
        return unwrap(await this.getLookupsRaw(), 'Procedures lookups not found');
    }
    getLookupsRaw() {
        return this.http.get(this.buildUrl('lookups'));
    }
    /** GET api/procedures/lookups/ministries: the "which ministry are you serving in?" picker. */
    async getMinistries() {
        return (await this.getMinistriesRaw()).data ?? [];
    }
    getMinistriesRaw() {
        return this.http.get(this.buildUrl('lookups/ministries'));
    }
    // ---- portal ----
    /** GET api/procedures/public/settings: portal name, public run / chat switches, reCAPTCHA site key, copy. */
    async getPublicSettings() {
        return unwrap(await this.getPublicSettingsRaw(), 'Procedures portal settings not found');
    }
    getPublicSettingsRaw() {
        return this.http.get(this.buildUrl('public/settings'));
    }
    /** GET api/procedures/public/categories: only categories that hold directory procedures. */
    async getPublicCategories() {
        return (await this.getPublicCategoriesRaw()).data ?? [];
    }
    getPublicCategoriesRaw() {
        return this.http.get(this.buildUrl('public/categories'));
    }
    /** GET api/procedures/public/directory: public procedures listed in the directory. */
    async getPublicDirectory(query = {}) {
        return unwrap(await this.getPublicDirectoryRaw(query), 'Procedures directory not found');
    }
    getPublicDirectoryRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('public/directory', {
            search: query.search,
            categoryID: query.categoryID,
            locationID: query.locationID,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** GET api/procedures/public/procedures/{code}: the published snapshot (403 sign_in_required when private). */
    async getPublicProcedure(code) {
        return unwrap(await this.getPublicProcedureRaw(code), `Procedure ${code} not found`);
    }
    getPublicProcedureRaw(code) {
        return this.http.get(this.buildUrl(`public/procedures/${this.enc(code)}`));
    }
    // ---- anonymous runs ----
    /**
     * POST api/procedures/public/procedures/{code}/runs: start an anonymous run. Keep the returned
     * `runToken` (it is only returned here) and pass it to every later public run call.
     */
    async startPublicRun(code, request) {
        return unwrap(await this.startPublicRunRaw(code, request), 'Failed to start the procedure');
    }
    startPublicRunRaw(code, request) {
        return this.http.post(this.buildUrl(`public/procedures/${this.enc(code)}/runs`), request);
    }
    /** GET api/procedures/public/runs/{runId} (X-Run-Token). */
    async getPublicRun(runId, runToken) {
        return unwrap(await this.getPublicRunRaw(runId, runToken), `Run ${runId} not found`);
    }
    getPublicRunRaw(runId, runToken) {
        return this.http.get(this.buildUrl(`public/runs/${runId}`), runTokenOptions(runToken));
    }
    /** PUT api/procedures/public/runs/{runId}/steps/{stepId} (X-Run-Token): tick, skip, report a problem or untick. */
    async updatePublicRunStep(runId, runToken, stepId, request) {
        return unwrap(await this.updatePublicRunStepRaw(runId, runToken, stepId, request), 'Failed to update the step');
    }
    updatePublicRunStepRaw(runId, runToken, stepId, request) {
        return this.http.put(this.buildUrl(`public/runs/${runId}/steps/${stepId}`), request, runTokenOptions(runToken));
    }
    /** POST api/procedures/public/runs/{runId}/complete (X-Run-Token). */
    async completePublicRun(runId, runToken, request = {}) {
        return unwrap(await this.completePublicRunRaw(runId, runToken, request), 'Failed to complete the run');
    }
    completePublicRunRaw(runId, runToken, request = {}) {
        return this.http.post(this.buildUrl(`public/runs/${runId}/complete`), request, runTokenOptions(runToken));
    }
    /** POST api/procedures/public/runs/{runId}/abandon (X-Run-Token). */
    async abandonPublicRun(runId, runToken, request = {}) {
        return unwrap(await this.abandonPublicRunRaw(runId, runToken, request), 'Failed to abandon the run');
    }
    abandonPublicRunRaw(runId, runToken, request = {}) {
        return this.http.post(this.buildUrl(`public/runs/${runId}/abandon`), request, runTokenOptions(runToken));
    }
    // ---- AI chat ----
    /**
     * POST api/procedures/public/procedures/{code}/chat: ask about this procedure (public documents only).
     * `runToken` is optional; when given it is sent as X-Run-Token alongside `request.runID`.
     */
    async askPublic(code, request, runToken) {
        return unwrap(await this.askPublicRaw(code, request, runToken), 'No answer returned');
    }
    askPublicRaw(code, request, runToken) {
        return this.http.post(this.buildUrl(`public/procedures/${this.enc(code)}/chat`), request, runTokenOptions(runToken));
    }
    /** GET api/procedures/public/procedures/{code}/chat/{sessionKey}?deviceId=: this device's chat history. */
    async getPublicChatHistory(code, sessionKey, deviceId) {
        return (await this.getPublicChatHistoryRaw(code, sessionKey, deviceId)).data ?? [];
    }
    getPublicChatHistoryRaw(code, sessionKey, deviceId) {
        return this.http.get(this.buildUrlWithParams(`public/procedures/${this.enc(code)}/chat/${this.enc(sessionKey)}`, { deviceId }));
    }
}
PublicProceduresService.DEFAULT_SERVICE_PATH = 'procedures';
PublicProceduresService.BASE_PATH = 'api/procedures';
//# sourceMappingURL=PublicProceduresService.js.map